import React, { useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import { useAuth } from "../contexts/AuthContext";
import { PaymentCardForm } from "./PaymentCardForm";
import { apiService, userApi } from "../services/api";
import { getUserAccessStatus } from "../utils";
import { APP_CONFIG } from "../config/app.config";

const stripePromise = loadStripe(
    process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || ""
);

interface SubscriptionModalProps {
    open: boolean;
    onClose: () => void;
    onSuccess?: () => void;
    skipFreeTrial?: boolean;
}

const SubscriptionModal: React.FC<SubscriptionModalProps> = ({
    open,
    onClose,
    onSuccess,
    skipFreeTrial = false,
}) => {
    const { dbUser, updateUserApiKey, refreshUser } = useAuth();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [step, setStep] = useState<"plan" | "payment" | "processing">("plan");
    
    // Determine if this is free trial mode
    const accessStatus = getUserAccessStatus(dbUser);
    const isFreeTrialMode = accessStatus.canGetFreeTrial && !skipFreeTrial;
    console.log("skipFreeTrial", skipFreeTrial);


    const handleSelectPlan = async () => {
        setError(null);
        setLoading(true);

        try {
            // For free trial mode, go directly to payment
            if (isFreeTrialMode) {
                setStep("payment");
                setLoading(false);
                return;
            }

            // First check if user already has an active subscription
            const subscriptionStatus =
                await apiService.subscriptions.getSubscriptionStatus();
            
            if (subscriptionStatus.has_subscription) {
                // User already has a subscription
                setError("You already have an active subscription");
                setLoading(false);
                return;
            }

            // If user has a Stripe customer ID, try to create subscription directly
            if (dbUser?.stripe_customer_id) {
                try {
                    // Try to create subscription
                    const subscriptionResponse =
                        await apiService.subscriptions.createSubscription();

                    if (subscriptionResponse.subscription_id || subscriptionResponse.passio_subscription_created) {
                        // Update the user's API key immediately if we have it
                        if (subscriptionResponse.passio_api_key) {
                            updateUserApiKey(subscriptionResponse.passio_api_key);
                        }
                        
                        // Subscription created successfully
                        if (onSuccess) {
                            onSuccess();
                        }
                        onClose();
                        return;
                    }
                } catch (err: any) {
                    // Check if error is specifically about missing payment method
                    const errorMessage = err.response?.data?.detail || "";
                    if (
                        errorMessage.toLowerCase().includes("payment method") ||
                        errorMessage.toLowerCase().includes("no payment")
                    ) {
                        // User needs to add payment method
                        setStep("payment");
                        setLoading(false);
                        return;
                    }
                    
                    // For other errors, show them
                    setError(errorMessage || "Failed to create subscription");
                    setLoading(false);
                    return;
                }
            } else {
                // No Stripe customer, need payment method
                setStep("payment");
            }
        } catch (err: any) {
            // Error checking subscription status
            console.error("Error checking subscription status:", err);
            // Proceed to payment step as fallback
            setStep("payment");
        } finally {
            setLoading(false);
        }
    };

    const handlePaymentSuccess = async (setupIntent: any) => {
        setStep("processing");
        setError(null);
        
        try {
            // Make sure the payment method was successfully saved
            if (!setupIntent || !setupIntent.payment_method) {
                throw new Error("Payment method setup failed");
            }

            // For free trial mode, activate the free trial instead of creating subscription
            if (isFreeTrialMode && dbUser?.id) {
                try {
                    await userApi.activateFreeTrial(dbUser.id);
                    await refreshUser();
                    
                    if (onSuccess) {
                        onSuccess();
                    }
                    onClose();
                    return;
                } catch (err: any) {
                    const errorMessage = err.response?.data?.detail || "Failed to activate free trial";
                    setError(errorMessage);
                    setStep("payment");
                    return;
                }
            }

            // Create subscription for non-free trial users
            const response =
                await apiService.subscriptions.createSubscription();

            if (response.subscription_id || response.passio_subscription_created) {
                // Update the user's API key immediately if we have it
                if (response.passio_api_key) {
                    updateUserApiKey(response.passio_api_key);
                }
                
                // Subscription created successfully
                if (onSuccess) {
                    onSuccess();
                }
                onClose();
            } else {
                throw new Error("Invalid subscription response");
            }
        } catch (err: any) {
            const errorMessage = err.response?.data?.detail || "Failed to create subscription";
            setError(errorMessage);
            
            // Check if it's a duplicate subscription error
            if (errorMessage.toLowerCase().includes("already have")) {
                // User already subscribed, just close
                setTimeout(() => {
                    if (onSuccess) {
                        onSuccess();
                    }
                    onClose();
                }, 2000);
            } else {
                // Go back to payment step for other errors
                setStep("payment");
            }
        } finally {
            setLoading(false);
        }
    };

    const handlePaymentError = (error: string) => {
        setError(error);
        // Stay on payment step to allow retry
    };

    if (!open) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                <div className="p-6">
                    <div className="flex justify-between items-center mb-6">
                        <h2 className="text-2xl font-bold">
                            {step === "plan" && (isFreeTrialMode ? "Start Your Free Trial" : "Choose Your Subscription")}
                            {step === "payment" && "Payment Information"}
                            {step === "processing" && "Processing..."}
                        </h2>
                        {step !== "processing" && (
                            <button
                                onClick={onClose}
                                className="text-gray-500 hover:text-gray-700"
                            >
                                <svg
                                    className="w-6 h-6"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M6 18L18 6M6 6l12 12"
                                    />
                                </svg>
                            </button>
                        )}
                    </div>

                    {error && (
                        <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
                            {error}
                        </div>
                    )}

                    {step === "plan" && (
                        <>
                            {isFreeTrialMode ? (
                                // Free Trial Design
                                <>
                                    <div className="text-center mb-6">             
                                        <p className="text-xl text-gray-600">
                                            {APP_CONFIG.FREE_TRIAL_RUNS} Free Meal Plans • No payment today • Cancel anytime
                                        </p>
                                    </div>

                                    <div className="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-400 rounded-xl p-6 mb-6 relative overflow-hidden">
                                        <div className="absolute top-0 right-0 bg-green-500 text-white px-4 py-1 rounded-bl-lg font-bold text-sm">
                                            $0 TODAY
                                        </div>
                                        
                                        <div className="flex items-center justify-between mb-6 mt-2">
                                            <div>
                                                <div className="text-6xl font-black text-green-600 leading-none">3</div>
                                                <div className="text-xl font-semibold text-gray-800">AI Meal Plans</div>
                                                <div className="text-sm text-gray-600 mt-1">Personalized for you</div>
                                            </div>
                                            <div className="text-right">
                                                <div className="text-gray-500 text-sm">Just Add Payment Method</div>
                                                <div className="text-3xl font-bold text-green-600">FREE</div>
                                                <div className="text-xs text-gray-600">No credit card charge</div>
                                            </div>
                                        </div>

                                        <div className="space-y-3 mb-6">
                                            <div className="flex items-start">
                                                <div className="bg-green-500 rounded-full p-1 mr-3 mt-0.5">
                                                    <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                    </svg>
                                                </div>
                                                <div>
                                                    <div className="font-semibold text-gray-800">Full Feature Access</div>
                                                    <div className="text-sm text-gray-600">AI generation, nutrition tracking, meal editing</div>
                                                </div>
                                            </div>
                                            <div className="flex items-start">
                                                <div className="bg-green-500 rounded-full p-1 mr-3 mt-0.5">
                                                    <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                    </svg>
                                                </div>
                                                <div>
                                                    <div className="font-semibold text-gray-800">Risk-Free Trial</div>
                                                    <div className="text-sm text-gray-600">No charges until you decide to subscribe</div>
                                                </div>
                                            </div>
                                            <div className="flex items-start">
                                                <div className="bg-green-500 rounded-full p-1 mr-3 mt-0.5">
                                                    <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                    </svg>
                                                </div>
                                                <div>
                                                    <div className="font-semibold text-gray-800">Save Your Meal Plans</div>
                                                    <div className="text-sm text-gray-600">Keep all your creations forever</div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="bg-white/70 rounded-lg p-3 text-center">
                                            <p className="text-sm text-gray-700">
                                                <span className="font-semibold">After free trial:</span> Just ${APP_CONFIG.SUBSCRIPTION_PRICE}/month (33% off) • Cancel anytime
                                            </p>
                                        </div>
                                    </div>

                                    <div className="bg-gray-50 rounded-lg p-4 mb-6">
                                        <div className="flex items-center justify-center space-x-6 text-sm text-gray-600">
                                            <div className="flex items-center">
                                                <svg className="w-5 h-5 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                                                </svg>
                                                Secure checkout
                                            </div>
                                            <div className="flex items-center">
                                                <svg className="w-5 h-5 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-8.293l-3-3a1 1 0 00-1.414 1.414L10.586 9.5H7a1 1 0 100 2h3.586l-1.293 1.293a1 1 0 101.414 1.414l3-3a1 1 0 000-1.414z" clipRule="evenodd" />
                                                </svg>
                                                Quick setup
                                            </div>
                                            <div className="flex items-center">
                                                <svg className="w-5 h-5 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                                    <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z" />
                                                </svg>
                                                Unlimited Profiles
                                            </div>
                                        </div>
                                    </div>
                                </>
                            ) : (
                                // Regular subscription design
                                <>
                                    <p className="text-gray-600 mb-6">
                                        Get unlimited access to our meal planning and nutrition tracking features
                                    </p>

                                    <div className="border-2 border-primary-500 rounded-lg p-6 bg-primary-50 mb-6">
                                        <div className="flex justify-between items-start mb-4">
                                            <div>
                                                <h3 className="text-xl font-semibold">
                                                    Monthly Subscription
                                                </h3>
                                                <p className="text-3xl font-bold text-primary-600 mt-2">
                                                    ${APP_CONFIG.SUBSCRIPTION_PRICE}/month
                                                </p>
                                                <span className="inline-flex items-center px-3 py-0.5 rounded-full text-sm font-medium bg-green-100 text-green-800 mt-2">
                                                    {APP_CONFIG.SUBSCRIPTION_DISCOUNT_PERCENT}% OFF
                                                </span>
                                            </div>
                                            <svg
                                                className="w-6 h-6 text-primary-500"
                                                fill="currentColor"
                                                viewBox="0 0 20 20"
                                            >
                                                <path
                                                    fillRule="evenodd"
                                                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                                    clipRule="evenodd"
                                                />
                                            </svg>
                                        </div>

                                        <p className="font-medium text-gray-700 mb-4">
                                            4 million tokens per month
                                        </p>
                                        <div className="space-y-2">
                                            <div className="flex items-center">
                                                <svg
                                                    className="w-5 h-5 text-green-500 mr-2"
                                                    fill="currentColor"
                                                    viewBox="0 0 20 20"
                                                >
                                                    <path
                                                        fillRule="evenodd"
                                                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                                        clipRule="evenodd"
                                                    />
                                                </svg>
                                                <span className="text-gray-700">
                                                    AI meal plan generation
                                                </span>
                                            </div>
                                            <div className="flex items-center">
                                                <svg
                                                    className="w-5 h-5 text-green-500 mr-2"
                                                    fill="currentColor"
                                                    viewBox="0 0 20 20"
                                                >
                                                    <path
                                                        fillRule="evenodd"
                                                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                                        clipRule="evenodd"
                                                    />
                                                </svg>
                                                <span className="text-gray-700">
                                                    Food search and nutrition tracking
                                                </span>
                                            </div>
                                            <div className="flex items-center">
                                                <svg
                                                    className="w-5 h-5 text-green-500 mr-2"
                                                    fill="currentColor"
                                                    viewBox="0 0 20 20"
                                                >
                                                    <path
                                                        fillRule="evenodd"
                                                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                                        clipRule="evenodd"
                                                    />
                                                </svg>
                                                <span className="text-gray-700">
                                                    Meal plan editor and creator
                                                </span>
                                            </div>
                                            <div className="flex items-center">
                                                <svg
                                                    className="w-5 h-5 text-green-500 mr-2"
                                                    fill="currentColor"
                                                    viewBox="0 0 20 20"
                                                >
                                                    <path
                                                        fillRule="evenodd"
                                                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                                        clipRule="evenodd"
                                                    />
                                                </svg>
                                                <span className="text-gray-700">
                                                    Discounted token refills when you go over
                                                </span>
                                            </div>
                                        </div>
                                        <p className="text-sm text-gray-500 mt-4">
                                            Normally $2.50 per million tokens
                                        </p>
                                    </div>
                                </>
                            )}

                            <div className="flex gap-4">
                                <button
                                    onClick={onClose}
                                    className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
                                    disabled={loading}
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleSelectPlan}
                                    className={`flex-1 px-4 py-2 ${isFreeTrialMode ? 'bg-green-600 hover:bg-green-700' : 'bg-primary-600 hover:bg-primary-700'} text-white rounded-md transition-colors disabled:bg-gray-400`}
                                    disabled={loading}
                                >
                                    {loading ? "Processing..." : (isFreeTrialMode ? "Add Payment Method" : "Continue")}
                                </button>
                            </div>
                        </>
                    )}

                    {step === "payment" && (
                        <Elements stripe={stripePromise}>
                            <PaymentCardForm
                                onSuccess={handlePaymentSuccess}
                                onError={handlePaymentError}
                                onCancel={() => setStep("plan")}
                            />
                        </Elements>
                    )}

                    {step === "processing" && (
                        <div className="text-center py-8">
                            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
                            <p className="mt-4 text-gray-600">
                                {isFreeTrialMode ? "Activating your free trial..." : "Setting up your subscription..."}
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SubscriptionModal;
